import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  ShieldCheck,
  Shield,
  User as UserIcon,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { adminApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { avatarColor, cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'
import type { AdminUser, AdminUserUpdateRequest } from '@/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

type BadgeKind = 'superuser' | 'staff' | 'user'

function roleBadgeCls(d: string, kind: BadgeKind): string {
  if (d === 'command') {
    return 'border border-primary/50 text-primary font-mono uppercase text-[10px] bg-transparent'
  }
  if (d === 'saas') {
    return 'bg-muted text-muted-foreground text-[11px]'
  }
  if (d === 'expressive') {
    // keep the hue, add rounded-full (already has it) — tinted pill variant
    const hueMap: Record<BadgeKind, string> = {
      superuser: 'bg-violet-500/15 text-violet-500 border border-violet-500/25 rounded-full',
      staff: 'bg-primary/15 text-primary border border-primary/25 rounded-full',
      user: 'bg-muted text-muted-foreground border border-border/60 rounded-full',
    }
    return hueMap[kind]
  }
  // current — return empty so caller uses its own literal classes
  return ''
}

function statusBadgeCls(d: string, active: boolean): string {
  if (d === 'command') {
    return active
      ? 'border border-primary/50 text-primary font-mono uppercase text-[10px] bg-transparent'
      : 'border border-border text-muted-foreground font-mono uppercase text-[10px] bg-transparent'
  }
  if (d === 'saas') {
    return active
      ? 'bg-muted text-muted-foreground text-[11px]'
      : 'bg-muted/60 text-muted-foreground text-[11px]'
  }
  if (d === 'expressive') {
    return active
      ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/25 rounded-full'
      : 'bg-muted/80 text-muted-foreground border border-border/60 rounded-full'
  }
  return ''
}

function RoleBadge({ user }: { user: AdminUser }) {
  const { t } = useTranslation('users')
  const d = useDirectionStore((s) => s.direction)

  if (user.is_superuser) {
    if (d === 'current') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-violet-500/15 text-violet-500 border border-violet-500/25">
          <ShieldCheck className="h-3 w-3" />
          {t('roles.superuser')}
        </span>
      )
    }
    return (
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 font-semibold', roleBadgeCls(d, 'superuser'))}>
        <ShieldCheck className="h-3 w-3" />
        {t('roles.superuser')}
      </span>
    )
  }
  if (user.is_staff) {
    if (d === 'current') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-primary/15 text-primary border border-primary/25">
          <Shield className="h-3 w-3" />
          {t('roles.staff')}
        </span>
      )
    }
    return (
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 font-semibold', roleBadgeCls(d, 'staff'))}>
        <Shield className="h-3 w-3" />
        {t('roles.staff')}
      </span>
    )
  }
  if (d === 'current') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-muted text-muted-foreground border border-border/60">
        <UserIcon className="h-3 w-3" />
        {t('roles.user')}
      </span>
    )
  }
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 font-semibold', roleBadgeCls(d, 'user'))}>
      <UserIcon className="h-3 w-3" />
      {t('roles.user')}
    </span>
  )
}

function StatusBadge({ active }: { active: boolean }) {
  const { t } = useTranslation('users')
  const d = useDirectionStore((s) => s.direction)
  if (d === 'current') {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
        active
          ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/25'
          : 'bg-muted/80 text-muted-foreground border-border/60'
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
        {active ? t('status.active') : t('status.inactive')}
      </span>
    )
  }
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 font-semibold', statusBadgeCls(d, active))}>
      <span className={cn('h-1.5 w-1.5 rounded-full',
        d === 'command'
          ? (active ? 'bg-[hsl(150,65%,45%)]' : 'bg-muted-foreground')
          : (active ? 'bg-emerald-500' : 'bg-muted-foreground')
      )} />
      {active ? t('status.active') : t('status.inactive')}
    </span>
  )
}

// ── User Form Sheet ───────────────────────────────────────────────────────────

interface UserFormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editUser?: AdminUser | null
  onSuccess: () => void
}

function UserFormSheet({ open, onOpenChange, editUser, onSuccess }: UserFormSheetProps) {
  const { t } = useTranslation('users')
  const { t: tc } = useTranslation('common')
  const isEdit = !!editUser
  const [username, setUsername] = useState(editUser?.username ?? '')
  const [email, setEmail] = useState(editUser?.email ?? '')
  const [firstName, setFirstName] = useState(editUser?.first_name ?? '')
  const [lastName, setLastName] = useState(editUser?.last_name ?? '')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)

  // Reset form when sheet opens/changes
  useState(() => {
    setUsername(editUser?.username ?? '')
    setEmail(editUser?.email ?? '')
    setFirstName(editUser?.first_name ?? '')
    setLastName(editUser?.last_name ?? '')
    setPassword('')
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (isEdit && editUser) {
        const data: AdminUserUpdateRequest = { email, first_name: firstName, last_name: lastName }
        if (password) data.password = password
        await adminApi.updateUser(editUser.id, data)
        toast.success(t('messages.updated'))
      } else {
        await adminApi.createUser({ username, email, password, first_name: firstName, last_name: lastName })
        toast.success(t('messages.created'))
      }
      onSuccess()
      onOpenChange(false)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string; username?: string[]; email?: string[]; password?: string[] } } })
        ?.response?.data
      const detail = msg?.detail ?? msg?.username?.[0] ?? msg?.email?.[0] ?? msg?.password?.[0] ?? t('messages.operationFailed')
      toast.error(detail)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={saving ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-sm border-l border-border bg-background shadow-elevation-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300 flex flex-col">
          <Dialog.Title className="sr-only">{isEdit ? t('form.editUser') : t('form.newUser')}</Dialog.Title>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60 flex-shrink-0">
            <div>
              <p className="text-sm font-semibold">{isEdit ? t('form.editUser') : t('form.newUser')}</p>
              {isEdit && <p className="text-xs text-muted-foreground">@{editUser?.username}</p>}
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <form id="user-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {!isEdit && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('form.username')} <span className="text-destructive">*</span></label>
                <Input value={username} onChange={e => setUsername(e.target.value)} placeholder={t('form.usernamePlaceholder')} required />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('form.email')} <span className="text-destructive">*</span></label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('form.emailPlaceholder')} required />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('form.firstName')}</label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t('form.firstNamePlaceholder')} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('form.lastName')}</label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t('form.lastNamePlaceholder')} />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('form.password')} {!isEdit && <span className="text-destructive">*</span>}
                {isEdit && <span className="text-xs text-muted-foreground font-normal ml-1">{t('form.passwordKeep')}</span>}
              </label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isEdit ? t('form.passwordEditPlaceholder') : t('form.passwordPlaceholder')}
                required={!isEdit}
              />
            </div>

          </form>

          {/* Footer */}
          <div className="flex gap-2 px-5 py-4 border-t border-border/60 flex-shrink-0">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={saving}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" form="user-form" className="flex-1" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? t('form.saveChanges') : t('form.createUser')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function UsersPage() {
  const { t } = useTranslation('users')
  const { user: currentUser } = useAuthStore()
  const qc = useQueryClient()
  const d = useDirectionStore((s) => s.direction)

  const [sheetOpen, setSheetOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      toast.success(t('messages.deleted'))
      setDeleteTarget(null)
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('messages.deleteFailed')
      toast.error(detail)
    },
  })

  const openCreate = () => { setEditTarget(null); setSheetOpen(true) }
  const openEdit = (u: AdminUser) => { setEditTarget(u); setSheetOpen(true) }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className={cn('text-heading', d === 'expressive' && 'font-display')}>{t('pageTitle')}</h1>
          <p className="text-body text-muted-foreground mt-0.5">{t('subtitle')}</p>
        </div>
        <Button onClick={openCreate} size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          {t('newUser')}
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t('stats.totalUsers'), value: users.length, icon: <Users className="h-4 w-4" />, accent: 'bg-primary/10', iconColor: 'text-primary', border: 'bg-primary' },
          { label: t('stats.active'), value: users.filter(u => u.is_active).length, icon: <UserIcon className="h-4 w-4" />, accent: 'bg-emerald-500/10', iconColor: 'text-emerald-600 dark:text-emerald-400', border: 'bg-emerald-500' },
          { label: t('stats.staff'), value: users.filter(u => u.is_staff).length, icon: <Shield className="h-4 w-4" />, accent: 'bg-amber-500/10', iconColor: 'text-amber-600 dark:text-amber-400', border: 'bg-amber-500' },
          { label: t('stats.superusers'), value: users.filter(u => u.is_superuser).length, icon: <ShieldCheck className="h-4 w-4" />, accent: 'bg-violet-500/10', iconColor: 'text-violet-600 dark:text-violet-400', border: 'bg-violet-500' },
        ].map(stat => (
          <Card key={stat.label} className={cn('overflow-hidden', d === 'expressive' && 'rounded-2xl')}>
            {/* current: colored strip; v2 directions: strips removed */}
            {d === 'current' && <div className={`h-0.5 w-full ${stat.border}`} />}
            <CardContent className={cn('pt-4 pb-3 px-4', d === 'command' && 'px-3 py-2', d === 'saas' && 'px-3 py-2')}>
              <div className="flex items-center justify-between mb-2">
                <p className={cn('text-label text-muted-foreground',
                  d === 'command' && 'text-[10px] uppercase tracking-wider font-mono',
                  d === 'saas' && 'text-[11px]',
                )}>
                  {stat.label}
                </p>
                {d === 'current' && (
                  <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${stat.accent} ${stat.iconColor}`}>
                    {stat.icon}
                  </div>
                )}
              </div>
              <p className={cn('text-heading',
                d === 'command' && 'font-mono tabular-nums text-primary',
                d === 'saas' && 'tabular-nums',
                d === 'expressive' && 'font-display',
              )}>
                {stat.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* User list/table */}
      <Card className={cn('border-border/60', d === 'expressive' && 'rounded-2xl overflow-hidden')}>
        <CardHeader className="pb-3">
          <CardTitle className={cn('text-body font-semibold flex items-center gap-2', d === 'expressive' && 'font-display')}>
            <Users className="h-4 w-4 text-muted-foreground" />
            {t('allUsers')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={cn('flex items-center gap-3 px-4 py-3 border-t border-border/40 first:border-t-0',
                  d === 'command' && 'px-3 py-1.5',
                  d === 'saas' && 'px-3 py-1.5',
                )}>
                  <div className="shimmer h-8 w-8 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="shimmer h-3.5 w-32 rounded" />
                    <div className="shimmer h-3 w-48 rounded" />
                  </div>
                  <div className="shimmer h-5 w-16 rounded-full" />
                  <div className="shimmer h-5 w-14 rounded-full" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium">{t('empty.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('empty.message')}</p>
            </div>
          ) : (
            <div className={cn((d === 'saas' || d === 'command') && 'divide-y divide-border')}>
              {users.map((u, idx) => {
                const isSelf = u.id === currentUser?.id
                const initials = u.username.slice(0, 2).toUpperCase()
                const bg = avatarColor(u.username)
                const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ')
                const joinDate = new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

                return (
                  <div
                    key={u.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors',
                      idx > 0 && d === 'current' && 'border-t border-border/40',
                      d === 'command' && 'px-3 py-1.5 font-mono text-[12px] hover:bg-accent/40',
                      d === 'saas' && 'px-3 py-1.5 text-[13px] hover:bg-muted',
                    )}
                  >
                    {/* Avatar */}
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${bg}`}>
                      {initials}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={cn('text-sm font-medium truncate', d === 'command' && 'font-mono text-[12px]')}>@{u.username}</span>
                        {isSelf && (
                          <span className="text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5">{t('badge.you')}</span>
                        )}
                      </div>
                      <p className={cn('text-xs text-muted-foreground truncate', d === 'command' && 'font-mono text-[11px]')}>
                        {fullName ? `${fullName} · ` : ''}{u.email}
                      </p>
                    </div>

                    {/* Badges */}
                    <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                      <RoleBadge user={u} />
                      <StatusBadge active={u.is_active} />
                    </div>

                    {/* Join date */}
                    <p className={cn('hidden md:block text-xs text-muted-foreground flex-shrink-0 w-24 text-right',
                      d === 'command' && 'font-mono text-[11px]',
                    )}>
                      {joinDate}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)} title={t('aria.editUser')}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/8"
                        onClick={() => setDeleteTarget(u)}
                        disabled={isSelf}
                        title={isSelf ? t('aria.cannotSelfDelete') : t('aria.deleteUser')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit sheet */}
      <UserFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editUser={editTarget}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
        title={t('dialog.deleteTitle')}
        description={t('dialog.deleteConfirm', { username: deleteTarget?.username })}
        confirmLabel={t('dialog.deleteTitle')}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </div>
  )
}
